import React, { useEffect, useState } from 'react';
import { signInWithPopup, signOut, onAuthStateChanged } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db, googleProvider } from './lib/firebase';
import AdminShell from './screens/super-admin/AdminShell.jsx';
import './AdminApp.css';

// ============================================================
// Aangan Admin — super-admin operations panel auth gate.
//
// URL: /?admin=1  (browser-only; Capacitor Android never reaches this)
//
// ACCESS CONTROL MODEL
// --------------------
// Firebase Auth: any Google account can sign in — that's intentional.
// The gate is NOT "who can reach the sign-in screen" (URL is not a secret).
// The gate is "who is authorized after sign-in".
//
// Authorization: the signed-in user's /users/{uid} doc must have
// superAdmin === 1 (number). Any other value or missing field = denied.
//
// Bypass prevention:
//   - firestore.rules blocklists the 'superAdmin' field in the
//     /users/{userId} update rule. Client cannot self-promote — the field
//     can only be set via Firebase Console or a server with admin SDK.
//   - Every admin-only Firestore collection (added in future sessions) must
//     independently gate reads/writes on this same field via rules. Client
//     UI is UX-only; Firestore rules are the actual security boundary.
//
// Live authorization: user doc subscribed via onSnapshot. Revoking the
// superAdmin field in Firestore Console kicks the session on next snapshot
// (within seconds), without requiring the user to refresh.
// ============================================================

export default function AdminApp() {
  // authStatus: 'loading' | 'signed-out' | 'authenticated'
  const [authStatus, setAuthStatus] = useState('loading');
  const [authUser, setAuthUser] = useState(null);
  // superAdminStatus: 'unknown' | 'granted' | 'denied'
  //   'unknown' — we're authenticated but haven't yet resolved the user doc
  //   'granted' — user doc exists and superAdmin === 1
  //   'denied'  — user doc missing, superAdmin field missing, or !== 1
  const [superAdminStatus, setSuperAdminStatus] = useState('unknown');
  const [signInError, setSignInError] = useState(null);

  // Subscribe to Firebase Auth state.
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      if (user) {
        setAuthUser(user);
        setAuthStatus('authenticated');
      } else {
        setAuthUser(null);
        setAuthStatus('signed-out');
        setSuperAdminStatus('unknown');
        setSignInError(null);
      }
    });
    return () => unsub();
  }, []);

  // Live subscription to the signed-in user's /users/{uid} doc. Fires
  // whenever the superAdmin field changes, so a Firestore-Console revoke
  // kicks the session immediately.
  useEffect(() => {
    if (!authUser?.uid) return;
    const userRef = doc(db, 'users', authUser.uid);
    const unsub = onSnapshot(
      userRef,
      (snap) => {
        if (snap.exists() && snap.data().superAdmin === 1) {
          setSuperAdminStatus('granted');
        } else {
          setSuperAdminStatus('denied');
        }
      },
      (err) => {
        // Read failure → treat as denied. Don't log permission-denied
        // (expected for accounts without a /users doc yet); log genuine
        // oddities so we notice real breakage.
        if (err?.code !== 'permission-denied') {
          console.warn('[AdminApp] user doc read failed:', err);
        }
        setSuperAdminStatus('denied');
      },
    );
    return () => unsub();
  }, [authUser?.uid]);

  const handleGoogleSignIn = async () => {
    setSignInError(null);
    try {
      // Force the Google account chooser to appear on every sign-in, even
      // when the browser already has an active Google session. Without
      // this, `signInWithPopup` silently reuses the last-used account and
      // "Sign out then Sign in" cycles back into the same account instantly
      // — surprising and wrong UX for an admin panel where account switching
      // must be deliberate. `prompt: 'select_account'` matches how GitHub,
      // GitLab, and other admin surfaces handle this.
      googleProvider.setCustomParameters({ prompt: 'select_account' });
      await signInWithPopup(auth, googleProvider);
      // onAuthStateChanged handles the rest of the state transitions.
    } catch (err) {
      if (err?.code === 'auth/popup-closed-by-user' || err?.code === 'auth/cancelled-popup-request') {
        // User dismissed — no-op, no error message.
        return;
      }
      console.error('[AdminApp] Google sign-in failed:', err);
      setSignInError('Sign-in failed. Try again.');
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
    } catch (err) {
      console.warn('[AdminApp] sign-out failed:', err);
    }
  };

  // ============================================================
  // Render
  // ============================================================

  // Loading covers two cases:
  //   1. Initial Firebase Auth state check pending (page just loaded)
  //   2. Authenticated but user-doc read pending (post-login, pre-authz)
  const isLoading =
    authStatus === 'loading' ||
    (authStatus === 'authenticated' && superAdminStatus === 'unknown');

  if (isLoading) {
    return (
      <div className="admin-shell">
        <div className="admin-loading">
          <div className="admin-spinner" />
          <div className="admin-loading-text">Checking access…</div>
        </div>
      </div>
    );
  }

  if (authStatus === 'signed-out') {
    return (
      <div className="admin-shell">
        <div className="admin-card">
          <div className="admin-brand">
            <div className="admin-brand-icon">🏛️</div>
            <div>
              <div className="admin-brand-name">Aangan Admin</div>
              <div className="admin-brand-sub">Operations panel</div>
            </div>
          </div>
          <div className="admin-body">
            <div className="admin-heading">Sign in to continue</div>
            <div className="admin-sub">
              Access is restricted to authorized team members.
            </div>
            <button className="admin-btn admin-btn-primary" onClick={handleGoogleSignIn}>
              <span className="admin-btn-g">G</span>
              <span>Sign in with Google</span>
            </button>
            {signInError && <div className="admin-error">{signInError}</div>}
          </div>
        </div>
      </div>
    );
  }

  if (authStatus === 'authenticated' && superAdminStatus === 'denied') {
    return (
      <div className="admin-shell">
        <div className="admin-card">
          <div className="admin-brand">
            <div className="admin-brand-icon">🏛️</div>
            <div>
              <div className="admin-brand-name">Aangan Admin</div>
              <div className="admin-brand-sub">Operations panel</div>
            </div>
          </div>
          <div className="admin-body">
            <div className="admin-heading admin-heading-denied">Access denied</div>
            <div className="admin-sub">
              This account is not authorized for the operations panel.
            </div>
            <div className="admin-user-line">
              Signed in as <b>{authUser?.email || 'unknown'}</b>
            </div>
            <button className="admin-btn admin-btn-secondary" onClick={handleSignOut}>
              Sign out
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Authorized — hand off to AdminShell for sidebar navigation and
  // page rendering. The auth gate above stays entirely in charge of
  // loading / signed-out / denied states; the shell only ever mounts
  // when we've resolved a real authorized user, so it never has to
  // handle those cases itself.
  return <AdminShell authUser={authUser} onSignOut={handleSignOut} />;
}
